/**
 * MHARHER Public Profile Page
 * Design cohérent avec l'ADN MHARHER - Style Sidi Bou Said
 * Moderne, professionnel, touches tunisiennes
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { 
  MapPin, Calendar, Trophy, Flame, Heart, Share2, Instagram,
  Youtube, ChefHat, Star, Copy, Check, Settings, MessageCircle,
  Grid3X3, BookOpen, Bookmark, MoreHorizontal, Users, Crown, BadgeCheck,
  Play, Zap, Eye, UserPlus, TrendingUp, Flag
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { AvatarUpload, CoverUpload } from '../components/ImageUpload';
import { TipButton } from '../components/TipButton';
import ReportDialog from '../components/moderation/ReportDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

// TikTok Icon
const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Configuration des niveaux - Style MHARHER
const LEVELS = {
  apprenti: { 
    label: 'Apprenti', 
    icon: '🌱', 
    color: '#94A3B8',
    bgColor: 'bg-slate-100',
    minBarka: 0,
    maxBarka: 100
  },
  commis: { 
    label: 'Commis', 
    icon: '🥄', 
    color: '#22C55E',
    bgColor: 'bg-green-100',
    minBarka: 100,
    maxBarka: 500
  },
  chef_de_partie: { 
    label: 'Chef de Partie', 
    icon: '👨‍🍳', 
    color: '#1E4ED8',
    bgColor: 'bg-blue-100',
    minBarka: 500,
    maxBarka: 1500
  },
  sous_chef: { 
    label: 'Sous Chef', 
    icon: '⭐', 
    color: '#8B5CF6',
    bgColor: 'bg-purple-100',
    minBarka: 1500,
    maxBarka: 5000
  },
  chef_de_cuisine: { 
    label: 'Chef de Cuisine', 
    icon: '👑', 
    color: '#F59E0B',
    bgColor: 'bg-amber-100',
    minBarka: 5000,
    maxBarka: 15000
  },
  maitre_harissa: { 
    label: 'Maître Harissa', 
    icon: '🔥', 
    color: '#DC2626',
    bgColor: 'bg-red-100',
    minBarka: 15000,
    maxBarka: Infinity
  },
};

// Configuration des badges
const BADGES = {
  creator: { label: 'Créateur', icon: Crown, color: 'bg-amber-500' },
  verified: { label: 'Vérifié', icon: BadgeCheck, color: 'bg-[#1E4ED8]' },
  founder: { label: 'Fondateur', icon: Star, color: 'bg-gray-800' },
  challenge_champion: { label: 'Champion', icon: Trophy, color: 'bg-yellow-500' },
  top_contributor: { label: 'Top', icon: TrendingUp, color: 'bg-green-500' },
  streak_master: { label: 'Streak', icon: Zap, color: 'bg-orange-500' },
};

// Fonction pour obtenir le niveau actuel
const getCurrentLevel = (barka) => {
  const levels = Object.entries(LEVELS);
  for (let i = levels.length - 1; i >= 0; i--) {
    if (barka >= levels[i][1].minBarka) {
      return { key: levels[i][0], ...levels[i][1] };
    }
  }
  return { key: 'apprenti', ...LEVELS.apprenti };
};

// Fonction pour obtenir la progression
const getLevelProgress = (barka) => {
  const current = getCurrentLevel(barka);
  const levels = Object.keys(LEVELS);
  const currentIndex = levels.indexOf(current.key);
  const nextLevelKey = levels[currentIndex + 1];
  
  if (!nextLevelKey) return { progress: 100, next: null, remaining: 0 };
  
  const nextLevel = LEVELS[nextLevelKey];
  const progress = ((barka - current.minBarka) / (nextLevel.minBarka - current.minBarka)) * 100;
  const remaining = nextLevel.minBarka - barka;
  
  return { 
    progress: Math.min(100, Math.max(0, progress)), 
    next: { key: nextLevelKey, ...nextLevel },
    remaining 
  };
};

// Composant Stat
function StatItem({ value, label, icon: Icon, iconColor, onClick }) {
  const Component = onClick ? 'button' : 'div';
  return (
    <Component 
      onClick={onClick}
      className={`text-center ${onClick ? 'hover:bg-gray-50 rounded-xl py-2 transition-colors cursor-pointer' : ''}`}
    >
      <div className={`w-10 h-10 rounded-xl ${iconColor} flex items-center justify-center mx-auto mb-2`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <p className="text-xl font-bold text-gray-900">{value.toLocaleString()}</p>
      <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
    </Component>
  );
}

// Composant Post Grid
function PostGrid({ posts, emptyMessage }) {
  if (!posts || posts.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-12 text-center">
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
          <Grid3X3 className="w-8 h-8 text-gray-400" />
        </div>
        <p className="text-gray-500">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-1 sm:gap-2">
      {posts.map((post, idx) => {
        const hasVideo = post.media_urls?.some(url => 
          url.includes('.mp4') || url.includes('.webm') || url.includes('video')
        );
        
        return (
          <motion.div
            key={post.id || idx}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: idx * 0.03 }}
            className="relative aspect-square rounded-xl overflow-hidden group cursor-pointer bg-gray-100"
          >
            {post.media_urls?.[0] ? (
              <img 
                src={post.media_urls[0]} 
                alt=""
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#1E4ED8]/5 to-[#1E4ED8]/10">
                <ChefHat className="w-8 h-8 text-gray-300" />
              </div>
            )}
            
            {/* Overlay au hover */}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <div className="flex items-center gap-4 text-white text-sm">
                <span className="flex items-center gap-1">
                  <Heart className="w-4 h-4" fill="white" />
                  {post.likes_count || 0}
                </span>
                <span className="flex items-center gap-1">
                  <MessageCircle className="w-4 h-4" />
                  {post.comments_count || 0}
                </span>
              </div>
            </div>
            
            {hasVideo && (
              <div className="absolute top-2 right-2">
                <Play className="w-5 h-5 text-white drop-shadow-lg" fill="white" />
              </div>
            )}
          </motion.div>
        );
      })}
    </div>
  );
}

// Composant Recipe Card
function RecipeCard({ recipe }) {
  return (
    <Link to={`/recipe/${recipe.id}`}>
      <motion.div
        whileHover={{ y: -4 }}
        className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all"
      >
        <div className="aspect-[4/3] relative overflow-hidden">
          <img 
            src={recipe.cover_image || recipe.image_url || '/placeholder.jpg'} 
            alt={recipe.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          <div className="absolute top-3 right-3">
            <Badge className="bg-white/90 text-gray-700 backdrop-blur-sm border-0">
              {'🌶️'.repeat(Math.min(recipe.spice_level || 1, 5))}
            </Badge>
          </div>
          <div className="absolute bottom-3 left-3 right-3">
            <h3 className="font-bold text-white text-lg line-clamp-2">{recipe.title}</h3>
          </div>
        </div>
        <div className="p-4 flex items-center justify-between text-sm">
          <div className="flex items-center gap-3 text-gray-500">
            <span className="flex items-center gap-1">
              <Heart className="w-4 h-4 text-red-400" />
              {recipe.likes_count || 0}
            </span>
            <span className="flex items-center gap-1">
              <Bookmark className="w-4 h-4 text-[#1E4ED8]" />
              {recipe.saves_count || 0}
            </span>
          </div>
          <Badge variant="outline" className="capitalize">{recipe.difficulty || 'facile'}</Badge>
        </div>
      </motion.div>
    </Link>
  );
}

// Modal Followers
function FollowModal({ isOpen, onClose, type, userId, api, t }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (isOpen && userId) {
      setLoading(true);
      api.get(`/users/${userId}/${type}`)
        .then(res => setUsers(res.data || []))
        .catch(() => setUsers([]))
        .finally(() => setLoading(false));
    }
  }, [isOpen, userId, type, api]);
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[70vh]">
        <DialogHeader>
          <DialogTitle className="text-center">
            {type === 'followers' ? t('common.followers', 'Abonnés') : t('common.following', 'Abonnements')}
          </DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto max-h-96 -mx-6 px-6">
          {loading ? (
            <div className="space-y-3 py-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="w-12 h-12 rounded-full" />
                  <div className="flex-1">
                    <Skeleton className="h-4 w-24 mb-1" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                </div>
              ))}
            </div>
          ) : users.length > 0 ? (
            <div className="space-y-1 py-2">
              {users.map(user => (
                <Link 
                  key={user.id} 
                  to={`/u/${user.username}`}
                  onClick={onClose}
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  <Avatar className="w-12 h-12 border-2 border-gray-100">
                    <AvatarImage src={user.profile_picture} />
                    <AvatarFallback className="bg-[#1E4ED8] text-white font-bold">
                      {(user.display_name || user.username)?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{user.display_name || user.username}</p>
                    <p className="text-sm text-gray-500 truncate">@{user.username}</p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">
              {type === 'followers' ? t('profile.noFollowers', 'Aucun abonné') : t('profile.noFollowing', 'Aucun abonnement')}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Composant Saved Content Tab
function SavedContentTab({ api, t }) {
  const [savedContent, setSavedContent] = useState({ recipes: [], posts: [], videos: [] });
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    fetchSavedContent();
  }, []);

  const fetchSavedContent = async () => {
    try {
      const response = await api.get('/saved/all');
      setSavedContent(response.data);
    } catch (error) {
      console.error('Failed to fetch saved content:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalItems = savedContent.recipes.length + savedContent.posts.length + savedContent.videos.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-[#1E4ED8] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (totalItems === 0) {
    return (
      <div className="bg-white rounded-2xl p-12 text-center">
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
          <Bookmark className="w-8 h-8 text-gray-400" />
        </div>
        <p className="text-gray-500 mb-2">{t('profile.noSavedContent', 'Rien de sauvegardé')}</p>
        <p className="text-sm text-gray-400">{t('profile.saveContentHint', 'Sauvegarde des recettes, posts et vidéos pour les retrouver ici')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <Button
          size="sm"
          variant={activeFilter === 'all' ? 'default' : 'outline'}
          onClick={() => setActiveFilter('all')}
          className={activeFilter === 'all' ? 'bg-[#1E4ED8]' : ''}
        >
          Tout ({totalItems})
        </Button>
        {savedContent.recipes.length > 0 && (
          <Button
            size="sm"
            variant={activeFilter === 'recipes' ? 'default' : 'outline'}
            onClick={() => setActiveFilter('recipes')}
            className={activeFilter === 'recipes' ? 'bg-[#1E4ED8]' : ''}
          >
            Recettes ({savedContent.recipes.length})
          </Button>
        )}
        {savedContent.posts.length > 0 && (
          <Button
            size="sm"
            variant={activeFilter === 'posts' ? 'default' : 'outline'}
            onClick={() => setActiveFilter('posts')}
            className={activeFilter === 'posts' ? 'bg-[#1E4ED8]' : ''}
          >
            Posts ({savedContent.posts.length})
          </Button>
        )}
        {savedContent.videos.length > 0 && (
          <Button
            size="sm"
            variant={activeFilter === 'videos' ? 'default' : 'outline'}
            onClick={() => setActiveFilter('videos')}
            className={activeFilter === 'videos' ? 'bg-[#1E4ED8]' : ''}
          >
            Vidéos ({savedContent.videos.length})
          </Button>
        )}
      </div>

      {/* Saved Recipes */}
      {(activeFilter === 'all' || activeFilter === 'recipes') && savedContent.recipes.length > 0 && (
        <div>
          {activeFilter === 'all' && (
            <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <ChefHat className="w-4 h-4" /> Recettes sauvegardées
            </h3>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {savedContent.recipes.map(recipe => (
              <RecipeCard key={recipe.id} recipe={recipe} />
            ))}
          </div>
        </div>
      )}

      {/* Saved Posts */}
      {(activeFilter === 'all' || activeFilter === 'posts') && savedContent.posts.length > 0 && (
        <div>
          {activeFilter === 'all' && (
            <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Grid3X3 className="w-4 h-4" /> Posts sauvegardés
            </h3>
          )}
          <PostGrid posts={savedContent.posts} emptyMessage="" />
        </div>
      )}

      {/* Saved Videos */}
      {(activeFilter === 'all' || activeFilter === 'videos') && savedContent.videos.length > 0 && (
        <div>
          {activeFilter === 'all' && (
            <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Play className="w-4 h-4" /> Vidéos sauvegardées
            </h3>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {savedContent.videos.map(video => (
              <Link key={video.id} to={`/feed/${video.id}`} className="group">
                <div className="relative aspect-[9/16] rounded-xl overflow-hidden bg-gray-200">
                  {video.thumbnail_url || video.media_url ? (
                    <img 
                      src={video.thumbnail_url || video.media_url} 
                      alt={video.caption}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-300">
                      <Play className="w-8 h-8 text-gray-500" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute bottom-2 left-2 right-2 text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity truncate">
                    {video.caption || 'Vidéo'}
                  </div>
                  <div className="absolute bottom-2 right-2 flex items-center gap-1 text-white text-xs">
                    <Eye className="w-3 h-3" />
                    {video.views_count || 0}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Composant Principal
export default function PublicProfilePage() {
  const { username } = useParams();
  const { api, user: currentUser, isAuthenticated, updateUser } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('posts');
  const [copied, setCopied] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [showFollowers, setShowFollowers] = useState(false);
  const [showFollowing, setShowFollowing] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);

  // Charger le profil
  const fetchProfile = useCallback(async () => {
    try {
      const res = await api.get(`/u/${username}`);
      setProfile(res.data.user);
      setRecipes(res.data.recipes || []);
      setIsFollowing(res.data.is_following);
      
      // Charger les posts
      try {
        const postsRes = await api.get(`/posts/user/${res.data.user.id}`);
        setPosts(postsRes.data.posts || []);
      } catch {
        setPosts([]);
      }
    } catch (err) {
      toast.error(t('errors.profileNotFound', 'Profil non trouvé'));
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [username, api, navigate, t]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Follow/Unfollow
  const handleFollow = async () => {
    if (!isAuthenticated) {
      toast.error(t('errors.loginRequired', 'Connectez-vous pour suivre'));
      navigate('/auth');
      return;
    }
    
    setFollowLoading(true);
    try {
      if (isFollowing) {
        await api.delete(`/users/${profile.id}/follow`);
        setIsFollowing(false);
        setProfile(p => ({ ...p, followers_count: Math.max(0, (p.followers_count || 0) - 1) }));
        toast.success(t('profile.unfollowed', 'Désabonné'));
      } else {
        await api.post(`/users/${profile.id}/follow`);
        setIsFollowing(true);
        setProfile(p => ({ ...p, followers_count: (p.followers_count || 0) + 1 }));
        toast.success(t('profile.followed', 'Abonné!'));
      }
    } catch {
      toast.error(t('errors.generic', 'Erreur'));
    } finally {
      setFollowLoading(false);
    }
  };

  // Partager
  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ 
          title: `${profile.display_name} - MHARHER`, 
          url 
        });
      } catch {}
    } else {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(t('profile.linkCopied', 'Lien copié!'));
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Upload handlers
  const handleAvatarUpload = async (imageData) => {
    await api.put('/users/profile', { profile_picture: imageData });
    setProfile(p => ({ ...p, profile_picture: imageData }));
    if (currentUser?.id === profile.id) updateUser({ profile_picture: imageData });
  };

  const handleCoverUpload = async (imageData) => {
    await api.put('/users/profile', { cover_image: imageData });
    setProfile(p => ({ ...p, cover_image: imageData }));
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F5F2] p-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl overflow-hidden">
            <Skeleton className="h-48 md:h-56" />
            <div className="p-6 -mt-16 relative">
              <Skeleton className="w-28 h-28 rounded-full mx-auto border-4 border-white" />
              <Skeleton className="h-6 w-40 mx-auto mt-4" />
              <Skeleton className="h-4 w-24 mx-auto mt-2" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) return null;

  const isOwnProfile = currentUser?.id === profile.id;
  const currentLevel = getCurrentLevel(profile.barka || 0);
  const levelProgress = getLevelProgress(profile.barka || 0);
  const joinDate = new Date(profile.created_at).toLocaleDateString(
    i18n.language === 'ar' ? 'ar-TN' : i18n.language === 'en' ? 'en-US' : 'fr-FR',
    { month: 'long', year: 'numeric' }
  );

  return (
    <div className="min-h-screen bg-[#F8F5F2]" data-testid="public-profile-page">
      {/* Navigation Bar - Sticky on mobile, normal flow on desktop */}
      <div className="sticky md:relative top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm md:shadow-none">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-gray-900"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('common.back', 'Retour')}
          </Button>
          
          <h1 className="font-semibold text-gray-900 truncate max-w-[150px] sm:max-w-none">
            @{profile?.username}
          </h1>
          
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
              className="text-gray-600 hover:text-gray-900"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </Button>
            {isOwnProfile && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/settings')}
                className="text-gray-600 hover:text-gray-900"
              >
                <Settings className="w-5 h-5" />
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-4 md:py-6">
        
        {/* Carte Profil Principale */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl overflow-hidden shadow-sm"
        >
          {/* Cover Image */}
          <div className="h-40 md:h-56 relative overflow-hidden">
            {isOwnProfile ? (
              <CoverUpload currentImage={profile.cover_image} onUpload={handleCoverUpload} />
            ) : profile.cover_image ? (
              <img src={profile.cover_image} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-[#1E4ED8] to-[#3B82F6]">
                {/* Pattern décoratif tunisien */}
                <div className="absolute inset-0 opacity-20">
                  <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <defs>
                      <pattern id="tunisian-pattern" width="20" height="20" patternUnits="userSpaceOnUse">
                        <circle cx="10" cy="10" r="1.5" fill="white"/>
                        <path d="M0,10 L10,0 L20,10 L10,20 Z" fill="none" stroke="white" strokeWidth="0.5" opacity="0.5"/>
                      </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#tunisian-pattern)"/>
                  </svg>
                </div>
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
          </div>

          {/* Contenu du profil */}
          <div className="relative px-4 sm:px-6 pb-6">
            {/* Avatar */}
            <div className="flex justify-center -mt-14 md:-mt-16 relative z-10">
              {isOwnProfile ? (
                <AvatarUpload 
                  currentImage={profile.profile_picture} 
                  onUpload={handleAvatarUpload}
                  size="xl"
                />
              ) : (
                <div className="relative">
                  <div 
                    className="w-28 h-28 md:w-32 md:h-32 rounded-full border-4 border-white shadow-xl overflow-hidden"
                    style={{ boxShadow: `0 0 0 4px ${currentLevel.color}20` }}
                  >
                    {profile.profile_picture ? (
                      <img src={profile.profile_picture} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-[#1E4ED8] flex items-center justify-center">
                        <span className="text-4xl font-bold text-white">
                          {(profile.display_name || profile.username)?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>
                  {/* Badge niveau */}
                  <div 
                    className="absolute -bottom-1 -right-1 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center text-xl"
                    title={currentLevel.label}
                  >
                    {currentLevel.icon}
                  </div>
                  {/* Badge vérifié */}
                  {profile.is_verified && (
                    <div className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-[#1E4ED8] flex items-center justify-center shadow-lg">
                      <BadgeCheck className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Infos utilisateur */}
            <div className="text-center mt-4">
              <div className="flex items-center justify-center gap-2 flex-wrap">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                  {profile.display_name || profile.username}
                </h1>
                {profile.is_creator && (
                  <Badge className="bg-gradient-to-r from-amber-400 to-orange-500 text-white border-0">
                    <Crown className="w-3 h-3 mr-1" />
                    Créateur
                  </Badge>
                )}
              </div>
              <p className="text-gray-500 mt-1">@{profile.username}</p>
              
              {/* Bio */}
              {profile.bio && (
                <p className="text-gray-600 mt-3 max-w-md mx-auto">{profile.bio}</p>
              )}
              
              {/* Méta */}
              <div className="flex items-center justify-center gap-4 mt-3 text-sm text-gray-500 flex-wrap">
                {profile.city && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {profile.city}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {t('profile.joined', 'Depuis')} {joinDate}
                </span>
              </div>

              {/* Badges */}
              {profile.badges?.length > 0 && (
                <div className="flex items-center justify-center gap-2 mt-4 flex-wrap">
                  {profile.badges.map(badge => {
                    const info = BADGES[badge];
                    if (!info) return null;
                    const Icon = info.icon;
                    return (
                      <Badge key={badge} className={`${info.color} text-white border-0`}>
                        <Icon className="w-3 h-3 mr-1" />
                        {info.label}
                      </Badge>
                    );
                  })}
                </div>
              )}

              {/* Liens sociaux */}
              {(profile.tiktok_username || profile.instagram_username || profile.youtube_username) && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  {profile.tiktok_username && (
                    <a 
                      href={`https://tiktok.com/@${profile.tiktok_username}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-full bg-gray-100 hover:bg-black hover:text-white transition-all"
                    >
                      <TikTokIcon className="w-5 h-5" />
                    </a>
                  )}
                  {profile.instagram_username && (
                    <a 
                      href={`https://instagram.com/${profile.instagram_username}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-full bg-gray-100 hover:bg-gradient-to-r hover:from-purple-500 hover:to-pink-500 hover:text-white transition-all"
                    >
                      <Instagram className="w-5 h-5" />
                    </a>
                  )}
                  {profile.youtube_username && (
                    <a 
                      href={`https://youtube.com/@${profile.youtube_username}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-full bg-gray-100 hover:bg-red-500 hover:text-white transition-all"
                    >
                      <Youtube className="w-5 h-5" />
                    </a>
                  )}
                </div>
              )}

              {/* Boutons d'action */}
              <div className="flex items-center justify-center gap-2 mt-5">
                {isOwnProfile ? (
                  <Button 
                    onClick={() => navigate('/settings')}
                    variant="outline"
                    className="rounded-full px-6"
                    data-testid="edit-profile-btn"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    {t('profile.editProfile', 'Modifier')}
                  </Button>
                ) : (
                  <>
                    <Button 
                      onClick={handleFollow}
                      disabled={followLoading}
                      className={`rounded-full px-6 ${
                        isFollowing 
                          ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' 
                          : 'bg-[#1E4ED8] text-white hover:bg-[#1842B8]'
                      }`}
                      data-testid="follow-btn"
                    >
                      {isFollowing ? (
                        <><Check className="w-4 h-4 mr-2" />{t('common.following', 'Abonné')}</>
                      ) : (
                        <><UserPlus className="w-4 h-4 mr-2" />{t('common.follow', 'Suivre')}</>
                      )}
                    </Button>
                    <TipButton 
                      creatorId={profile.id} 
                      creatorName={profile.display_name || profile.username}
                    />
                  </>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" className="rounded-full">
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center">
                    <DropdownMenuItem onClick={handleShare}>
                      <Share2 className="w-4 h-4 mr-2" />
                      {t('common.share', 'Partager')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                      setCopied(true);
                      toast.success(t('profile.linkCopied', 'Lien copié!'));
                      setTimeout(() => setCopied(false), 2000);
                    }}>
                      {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                      {t('profile.copyLink', 'Copier le lien')}
                    </DropdownMenuItem>
                    {isAuthenticated && profile?.id !== currentUser?.id && (
                      <DropdownMenuItem 
                        onClick={() => setShowReportDialog(true)}
                        className="text-red-600"
                      >
                        <Flag className="w-4 h-4 mr-2" />
                        {t('common.report', 'Signaler')}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-2 sm:gap-4 mt-6 pt-6 border-t border-gray-100">
              <StatItem 
                value={profile.barka || 0} 
                label="BARKA" 
                icon={Flame} 
                iconColor="bg-gradient-to-br from-orange-400 to-red-500"
                onClick={() => navigate('/wallet')}
                data-testid="barka-stat-btn"
              />
              <button onClick={() => setShowFollowers(true)} className="text-center hover:bg-gray-50 rounded-xl py-2 transition-colors">
                <div className="w-10 h-10 rounded-xl bg-[#1E4ED8] flex items-center justify-center mx-auto mb-2">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <p className="text-xl font-bold text-gray-900">{(profile.followers_count || 0).toLocaleString()}</p>
                <p className="text-xs text-gray-500 uppercase tracking-wide">{t('common.followers', 'Abonnés')}</p>
              </button>
              <StatItem 
                value={profile.recipes_created || recipes.length || 0} 
                label={t('profile.recipes', 'Recettes')} 
                icon={ChefHat} 
                iconColor="bg-gradient-to-br from-green-400 to-emerald-500"
                onClick={() => setActiveTab('recipes')}
              />
              <StatItem 
                value={profile.challenges_won || 0} 
                label={t('profile.victories', 'Victoires')} 
                icon={Trophy} 
                iconColor="bg-gradient-to-br from-yellow-400 to-amber-500"
                onClick={() => navigate('/challenges')}
              />
            </div>

            {/* Barre de niveau */}
            <div className="mt-6 bg-gray-50 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div 
                    className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl ${currentLevel.bgColor}`}
                  >
                    {currentLevel.icon}
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">{currentLevel.label}</p>
                    <p className="text-sm text-gray-500">{(profile.barka || 0).toLocaleString()} BARKA</p>
                  </div>
                </div>
                {levelProgress.next && (
                  <div className="text-right">
                    <p className="text-xs text-gray-400">{t('profile.nextLevel', 'Prochain')}</p>
                    <p className="font-semibold" style={{ color: levelProgress.next.color }}>
                      {levelProgress.next.icon} {levelProgress.next.label}
                    </p>
                  </div>
                )}
              </div>
              
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ width: `${levelProgress.progress}%` }}
                  transition={{ duration: 1, ease: "easeOut" }}
                  className="h-full rounded-full"
                  style={{ backgroundColor: currentLevel.color }}
                />
              </div>
              
              {levelProgress.next && (
                <p className="text-xs text-gray-400 mt-2 text-center">
                  {levelProgress.remaining.toLocaleString()} BARKA {t('profile.toNextLevel', 'pour')} {levelProgress.next.label}
                </p>
              )}
            </div>

            {/* Stats Créateur */}
            {profile.is_creator && (
              <div className="mt-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-4 border border-amber-200">
                <div className="flex items-center gap-2 mb-3">
                  <Crown className="w-5 h-5 text-amber-600" />
                  <span className="font-semibold text-amber-800">{t('profile.creatorStats', 'Stats Créateur')}</span>
                  <Badge className="bg-amber-200 text-amber-800 border-0 capitalize ml-auto">
                    {profile.creator_tier || 'Bronze'}
                  </Badge>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-amber-700">{profile.challenges_won || 0}</p>
                    <p className="text-xs text-amber-600">{t('profile.challengesWon', 'Défis')}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-amber-700">{profile.total_views || 0}</p>
                    <p className="text-xs text-amber-600">{t('profile.totalViews', 'Vues')}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-amber-700">{profile.streak_days || 0}</p>
                    <p className="text-xs text-amber-600">{t('profile.streakDays', 'Streak')}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Tabs Contenu */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mt-4"
        >
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full bg-white rounded-2xl p-1.5 shadow-sm">
              <TabsTrigger 
                value="posts" 
                className="flex-1 rounded-xl py-2.5 data-[state=active]:bg-[#1E4ED8] data-[state=active]:text-white transition-all"
              >
                <Grid3X3 className="w-4 h-4 mr-2" />
                {t('profile.posts', 'Posts')}
                {posts.length > 0 && <span className="ml-1 opacity-70">({posts.length})</span>}
              </TabsTrigger>
              <TabsTrigger 
                value="recipes" 
                className="flex-1 rounded-xl py-2.5 data-[state=active]:bg-[#1E4ED8] data-[state=active]:text-white transition-all"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                {t('profile.recipes', 'Recettes')}
                {recipes.length > 0 && <span className="ml-1 opacity-70">({recipes.length})</span>}
              </TabsTrigger>
              {isOwnProfile && (
                <TabsTrigger 
                  value="saved" 
                  className="flex-1 rounded-xl py-2.5 data-[state=active]:bg-[#1E4ED8] data-[state=active]:text-white transition-all"
                >
                  <Bookmark className="w-4 h-4 mr-2" />
                  {t('profile.saved', 'Sauvegardés')}
                </TabsTrigger>
              )}
            </TabsList>

            <div className="mt-4">
              <TabsContent value="posts" className="mt-0">
                <PostGrid posts={posts} emptyMessage={t('profile.noPosts', 'Aucun post')} />
              </TabsContent>

              <TabsContent value="recipes" className="mt-0">
                {recipes.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {recipes.map(recipe => (
                      <RecipeCard key={recipe.id} recipe={recipe} />
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-2xl p-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                      <ChefHat className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500">{t('profile.noRecipes', 'Aucune recette')}</p>
                  </div>
                )}
              </TabsContent>

              {isOwnProfile && (
                <TabsContent value="saved" className="mt-0">
                  <SavedContentTab api={api} t={t} />
                </TabsContent>
              )}
            </div>
          </Tabs>
        </motion.div>
      </div>

      {/* Modals */}
      <FollowModal 
        isOpen={showFollowers}
        onClose={() => setShowFollowers(false)}
        type="followers"
        userId={profile?.id}
        api={api}
        t={t}
      />
      <FollowModal 
        isOpen={showFollowing}
        onClose={() => setShowFollowing(false)}
        type="following"
        userId={profile?.id}
        api={api}
        t={t}
      />
      
      {/* Report Dialog */}
      {showReportDialog && profile && (
        <ReportDialog
          open={showReportDialog}
          onOpenChange={setShowReportDialog}
          targetType="user"
          targetId={profile.id}
          targetName={profile.display_name || profile.username}
        />
      )}
    </div>
  );
}
